import { useMutation } from "@apollo/client";
import { FormEvent, useState } from "react";
import {
  SaveOrgWideCareerTrackMutation,
  SaveOrgWideCareerTrackMutationVariables,
} from "types/graphql-schema";

import createOrUpdateCareerTrackMutation from "@apps/org-settings/graphql/create-or-update-career-track-mutation";
import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import Input, { InputLabel } from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOptionType,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";

import { CareerTrackPickerTrack } from "./career-track-picker";

const CareerTrackCreateModal = ({
  organizationId,
  onClose,
  onCareerTrackCreated,
}: {
  organizationId: number;
  onClose: () => void;
  onCareerTrackCreated: (careerTrack: CareerTrackPickerTrack) => void;
}) => {
  const label = useLabel();
  const [title, setTitle] = useState("");
  const [team, setTeam] = useState<UserComboboxTeamOption | null>(null);
  const { options, query, setQuery } = useUserComboboxQuery({
    types: [UserComboboxOptionType.TEAM],
    selected: team ?? undefined,
  });

  const [saveCareerTrack, { loading, error }] = useMutation<
    SaveOrgWideCareerTrackMutation,
    SaveOrgWideCareerTrackMutationVariables
  >(createOrUpdateCareerTrackMutation, {
    refetchQueries: [getOrganizationCareerTracksQuery],
    onCompleted: (response) => {
      const careerTrack = response.createOrUpdateCareerTrack?.careerTrack;
      if (careerTrack) {
        onCareerTrackCreated({ id: careerTrack.id, title: careerTrack.title });
        onClose();
      }
    },
  });

  const canSave = title.trim().length > 0 && !loading;

  const handleSave = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    saveCareerTrack({
      variables: {
        organizationId,
        title: title.trim(),
        teamId: team?.id,
      },
    });
  };

  return (
    <Modal open onClose={onClose} aria-label="Create career track modal">
      <div className="p-6 flex flex-col gap-6">
        <ModalTitle onClose={onClose}>New Career Track</ModalTitle>
        <form className="flex flex-col gap-6" onSubmit={handleSave}>
          <Input
            autoFocus
            label="Name"
            aria-label="Create career track modal: Name"
            value={title}
            disabled={loading}
            onChange={(evt) => setTitle(evt.target.value)}
          />
          <div>
            <InputLabel
              label={`Associated ${label("team")} (optional)`}
              className="mb-1"
            />
            <UserCombobox
              disabled={loading}
              options={options}
              value={team}
              onChangeValue={(opt) => setTeam(opt as UserComboboxTeamOption)}
              query={query}
              onChangeQuery={setQuery}
              clearable
              onClearValue={() => setTeam(null)}
              placeholder="None"
              searchPlaceholder={`Search ${label("team", {
                pluralize: true,
              })}...`}
            />
          </div>
          {error && <GraphqlError error={error} />}
          <div className="flex items-center justify-end gap-2">
            <Button theme={buttonTheme.text} text="Cancel" onClick={onClose} />
            <Button
              type="submit"
              theme={buttonTheme.primary}
              disabled={!canSave}
              text="Create"
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CareerTrackCreateModal;
